import type { AllergenMapping } from "api-types";
import {
  useFirestoreMetadata,
  useMenuAppMetadataQuery,
  useThrowAsyncError,
} from "igfd-commons";
import { CircularProgressCenter } from "igfd-ui";

import React, { useCallback } from "react";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router-dom";

import { useAxiosClient } from "igfd-commons/src/http";

import { useLanguage } from "@dashboard/hooks";
import { useSyncState } from "@dashboard/hooks/useSyncState";
import { useTags } from "@dashboard/hooks/useTag";
import {
  InitialFetchThunkDispatch,
  initialFetchForReduxState,
} from "@dashboard/store/actions/initialFetchAction";
import type { ReduxState } from "@dashboard/types";

import { TaxonomyProvider } from "../context/TaxonomyContext";
import { useRestaurant } from "../hooks/useRestaurant";

/**
 * The core pages for editing menus and dishes, depend on similar metadata.
 * This is the part of the app that effecitvely supports multiple languages,
 * parses allergens and preferences differently compared to customer facing pages,
 * and needs allergen mappings present in the front end for basic functionality.
 *
 * This layout ensures that this metadata is loaded once and is shared between
 * the two pages.
 *
 * This layout assumes that the user is already logged in, the initial restaurant list
 * has been pulled in and the user has selected a restaurant. The restaurant can have no data
 * but the restaurant itself exists.
 */
export const MenusAndDishesLayout = () => {
  const dispatch: InitialFetchThunkDispatch = useDispatch();
  const throwAsyncError = useThrowAsyncError();

  const restaurant = useRestaurant();
  useSyncState();
  const initialMenusLoading = useSelector(
    (state: ReduxState) => state.menu.initialMenusLoading
  );
  const initialProductsLoading = useSelector(
    (state: ReduxState) => state.product.initialProductsLoading
  );
  const initialDishesLoading = useSelector(
    (state: ReduxState) => state.dish.initialDishesLoading
  );
  const initialResolvedEdiblesLoading = useSelector(
    (state: ReduxState) => state.resolvedEdibles.initialResolvedEdiblesLoading
  );
  const initialLocationsLoading = useSelector(
    (state: ReduxState) => state.location.initialLocationsLoading
  );

  const currentLanguage = useLanguage();
  const fetchStateFn = useCallback(
    () => dispatch(initialFetchForReduxState()),
    [dispatch]
  );

  // We use react query to refetch the initialFetchForReduxState from
  // time to time to keep the data fresh
  // Also when the user refocuses the tab, after some idle time, we want to refresh the data
  // When a restaurant changes, we want to refresh the data as well
  useQuery(["refreshingFetch", restaurant.uuid], fetchStateFn, {
    refetchInterval: 1000 * 60 * 3, // 3 minutes
  });

  /**
   * This is all allergens and preferences known in the system.
   *
   * This is used as metadata for various parts of the app.
   */
  const client = useAxiosClient({ withAuth: false });
  const menuAppMetadata = useMenuAppMetadataQuery(client);

  const allergenMappings = useFirestoreMetadata<AllergenMapping>(
    currentLanguage,
    "allergen-mappings"
  );

  const tagsResponse = useTags(restaurant.uuid);

  const isLoading =
    initialMenusLoading ||
    initialProductsLoading ||
    initialDishesLoading ||
    initialResolvedEdiblesLoading ||
    initialLocationsLoading ||
    allergenMappings.isLoading ||
    menuAppMetadata.isLoading ||
    tagsResponse.isLoading;

  if (isLoading) {
    return <CircularProgressCenter />;
  }

  if (!allergenMappings.data || !menuAppMetadata.data) {
    // We shouldn't render the menus and dishes layout without allergen and taxonomy data
    throwAsyncError(
      new Error(
        "Failed to load allergen mappings, taxonomy allergens or taxonomy preferences"
      )
    );
  }

  // When the all menus and all dishes have been loaded, render the actual page
  return (
    <TaxonomyProvider
      allergenMappings={allergenMappings.data!}
      taxonomyAllergens={menuAppMetadata.data!.allergens}
      taxonomyPreferences={menuAppMetadata.data!.preferences}
      tags={tagsResponse.data || []}
    >
      <Outlet key={restaurant.uuid} />
    </TaxonomyProvider>
  );
};
